import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";

import { ErrorAlert } from "../components/ErrorAlert";
import { OpenerStyleEditor } from "../components/OpenerStyleEditor";
import { useOpenerStyleWrites } from "../lib/openerStyles";
import { useTRPC } from "../lib/trpc";

/**
 * Opener styles → one style: creates (`/opener-styles/new`) or edits (`/opener-styles/$styleId`)
 * a platform opener style. There is no host theme here, so the preview shows the opener's own
 * palette. Platform admins only.
 */
export function PlatformOpenerStylePage() {
  const { styleId } = useParams({ strict: false }) as { styleId?: string };
  const trpc = useTRPC();
  const navigate = useNavigate();
  const query = useQuery(trpc.platform.openerStyles.list.queryOptions());
  const toList = () => void navigate({ to: "/opener-styles" });
  const { create, update } = useOpenerStyleWrites("platform", { onSaved: toList });

  if (query.error) return <ErrorAlert message={query.error.message} />;
  if (!query.data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const style = styleId ? query.data.styles.find((s) => s.id === styleId) : null;
  if (style === undefined) return <ErrorAlert message="No opener style with that id." />;

  return (
    <OpenerStyleEditor
      key={style?.id ?? "new"}
      style={style}
      hostTheme={undefined}
      hostLabel="each storefront's theme (the preview shows the opener's own palette)"
      // A platform page has no header trail, so it owns its own.
      crumb={
        <Link to="/opener-styles" className="text-muted-foreground hover:underline">
          Opener styles
        </Link>
      }
      saving={create.isPending || update.isPending}
      onCancel={toList}
      onSave={(values) =>
        style ? update.mutate({ styleId: style.id, ...values }) : create.mutate(values)
      }
    />
  );
}
